import type { User, UserOrganization, AccessLevel } from '@/types'
import { Timestamp, collection, getDocs, orderBy, query } from 'firebase/firestore'
import { db } from '@/lib/firebase'

/**
 * Backward compatibility helpers voor multi-org support
 * Deze functies zorgen ervoor dat oude code blijft werken met nieuwe User structuur
 */

/**
 * Krijg de actieve organisatie ID van een user
 * Valt terug op orgId voor backward compatibility
 */
export function getUserActiveOrgId(user: User | null | undefined): string {
  if (!user) return ''
  return user.activeOrgId || user.orgId || user.organizations?.[0]?.orgId || ''
}

/**
 * Krijg het toegangsniveau van een user in de actieve organisatie
 * Valt terug op toegangsniveau voor backward compatibility
 */
export function getUserAccessLevel(user: User | null | undefined): AccessLevel {
  if (!user) return 'user'

  // Platform-admin is een globale rol, ongeacht actieve org
  if (user.toegangsniveau === 'platform-admin') return 'platform-admin'

  // Nieuwe structuur: zoek rol in actieve organisatie
  if (user.activeOrgId && user.organizations) {
    const activeOrg = user.organizations.find(o => o.orgId === user.activeOrgId)
    if (activeOrg) return activeOrg.role
  }

  // Fallback naar eerste organisatie
  if (user.organizations && user.organizations.length > 0) {
    return user.organizations[0].role
  }

  // Backward compatibility: oude toegangsniveau veld
  return user.toegangsniveau || 'user'
}

/**
 * Krijg het domein van een user in de actieve organisatie
 */
export function getUserDomain(user: User | null | undefined): string {
  if (!user) return ''

  // Nieuwe structuur
  if (user.activeOrgId && user.organizations) {
    const activeOrg = user.organizations.find(o => o.orgId === user.activeOrgId)
    if (activeOrg) return activeOrg.domein
  }

  // Fallback
  if (user.organizations && user.organizations.length > 0) {
    return user.organizations[0].domein
  }

  return user.domein || user.email.split('@')[1] || ''
}

/**
 * Krijg de rol van een user in een specifieke organisatie
 * Gebruikt voor het tonen/wijzigen van rollen van andere users in een bepaalde org
 */
export function getUserRoleInOrg(user: User | null | undefined, orgId: string): AccessLevel {
  if (!user) return 'user'
  if (user.toegangsniveau === 'platform-admin') return 'platform-admin'
  const org = user.organizations?.find(o => o.orgId === orgId && o.status === 'actief')
  if (org) return org.role
  // Legacy fallback — alleen als er géén organizations entry is voor deze org
  if (!user.organizations?.some(o => o.orgId === orgId) && user.orgId === orgId) {
    return user.toegangsniveau || 'user'
  }
  return 'user'
}

/**
 * Vakgroep-lidmaatschap van een user binnen één specifieke organisatie.
 * Analoog aan getUserRoleInOrg: leest bij voorkeur de org-gebonden organizations[].vakgroepIds,
 * en valt enkel terug op het legacy enkelvoudige (org-ambigue) vakgroepId als er nog geen
 * organizations-entry met vakgroepIds bestaat voor deze org.
 */
export function getUserVakgroepIdsForOrg(user: User | null | undefined, orgId: string): string[] {
  if (!user) return []
  const org = user.organizations?.find(o => o.orgId === orgId)
  if (org?.vakgroepIds) return org.vakgroepIds
  if (!user.organizations?.some(o => o.orgId === orgId) && user.orgId === orgId && user.vakgroepId) {
    return [user.vakgroepId]
  }
  return []
}

// GO!-gebruikersrol: zelfde org-gebonden opzet als vakgroepIds, maar geen legacy
// single-value fallback nodig — dit veld bestond nooit vóór multi-org.
export function getUserGoGebruikersrolIdsForOrg(user: User | null | undefined, orgId: string): string[] {
  if (!user) return []
  const org = user.organizations?.find(o => o.orgId === orgId)
  return org?.goGebruikersrolIds || []
}

/**
 * Vakgroep-snapshot van een document (TrainingRequest/Registration/TrainingEvaluation) op
 * het moment van aanmaak. Valt terug op het legacy enkelvoudige snapshotveld als het nieuwe
 * meervoudige veld nog ontbreekt (oudere documenten, of gebruiker had toen maar 1 vakgroep).
 */
export function getSnapshotVakgroepIds(doc: { deelnemerVakgroepIds?: string[]; deelnemerVakgroepId?: string }): string[] {
  if (doc.deelnemerVakgroepIds && doc.deelnemerVakgroepIds.length > 0) return doc.deelnemerVakgroepIds
  if (doc.deelnemerVakgroepId) return [doc.deelnemerVakgroepId]
  return []
}

/**
 * Check of user actief lid is van een organisatie.
 * Leest organizations[] (met status 'actief'); enkel accounts zonder
 * organizations-entry vallen terug op het legacy orgId-veld — het
 * gedenormaliseerde orgIds[]-veld kan afwijken van deze brondata
 * (zie memory orgids-denormalisatie-drift) en wordt hier bewust niet gebruikt.
 */
export function isActiveMemberOfOrg(user: User | null | undefined, orgId: string): boolean {
  if (!user) return false
  if (user.organizations?.length) {
    return user.organizations.some(o => o.orgId === orgId && o.status === 'actief')
  }
  return user.orgId === orgId
}

/**
 * Haal alle actieve leden van een organisatie op, gebaseerd op organizations[]
 * i.p.v. het driftgevoelige orgIds[]-veld. Vervangt overal de vroegere
 * `where('orgIds', 'array-contains', orgId)`-query.
 */
export async function fetchOrgMembers(orgId: string): Promise<User[]> {
  const snap = await getDocs(query(collection(db, 'users'), orderBy('naam')))
  return snap.docs.map(d => d.data() as User).filter(u => isActiveMemberOfOrg(u, orgId))
}

/**
 * Krijg alle actieve organisaties van een user
 */
export function getUserActiveOrganizations(user: User | null | undefined): UserOrganization[] {
  if (!user || !user.organizations) return []
  return user.organizations.filter(o => o.status === 'actief')
}

/**
 * Check of user multi-org heeft (meer dan 1 actieve organisatie)
 */
export function hasMultipleActiveOrgs(user: User | null | undefined): boolean {
  return getUserActiveOrganizations(user).length > 1
}

/**
 * Migreer oude user structuur naar nieuwe multi-org structuur
 * Dit wordt gebruikt bij eerste login na deployment
 */
export function migrateUserToMultiOrg(user: User): Partial<User> {
  // Als user al nieuwe structuur heeft, skip
  if (user.organizations && user.organizations.length > 0) {
    return {}
  }

  // Als user oude structuur heeft (orgId + toegangsniveau)
  if (user.orgId && user.toegangsniveau) {
    const newOrg: UserOrganization = {
      orgId: user.orgId,
      role: user.toegangsniveau,
      domein: user.domein || user.email.split('@')[1],
      status: user.status === 'vertrokken' ? 'vertrokken' : 'actief',
      addedAt: user.aangemaakt || Timestamp.now()
    }

    if (user.status === 'vertrokken') {
      newOrg.leftAt = Timestamp.now()
    }

    return {
      organizations: [newOrg],
      activeOrgId: user.orgId,
      // Behoud oude velden voor compatibility, maar ze worden computed
    }
  }

  return {}
}

/**
 * Vind of merge organisatie voor een user
 * Gebruikt tijdens login om te checken of user al lid is van org
 */
export function findOrAddOrganization(
  user: User,
  orgId: string,
  role: AccessLevel,
  domein: string
): { organizations: UserOrganization[], activeOrgId?: string, needsUpdate: boolean } {
  const existingOrgs = user.organizations || []

  // Check of org al bestaat
  const existingOrg = existingOrgs.find(o => o.orgId === orgId)

  if (existingOrg) {
    // Org bestaat al, update status naar actief als vertrokken. leftAt wordt
    // via destructuring weggelaten i.p.v. op undefined gezet — Firestore's
    // updateDoc() weigert elke undefined-waarde, ook genest in een array
    // (ontdekt 4 okt 2026: "Unsupported field value: undefined" bij een
    // heractiverende login voor een org waar de user eerder uit vertrok).
    //
    // role komt mee van de huidige, verse `role`-parameter i.p.v. de oude
    // opgeslagen rol te behouden — anders heractiveert een login een lang
    // geleden ingetrokken rol (bv. platform-admin) ongezien opnieuw, zelfs
    // nadat een beheerder die expliciet via de Gebruikers-tab verwijderde
    // (ontdekt 4 okt 2026, zie memory toegangsniveau-sync-removeuserfromorg).
    if (existingOrg.status === 'vertrokken') {
      const updatedOrgs = existingOrgs.map(o => {
        if (o.orgId !== orgId) return o
        const { leftAt: _leftAt, ...rest } = o
        return { ...rest, status: 'actief' as const, role }
      })
      return {
        organizations: updatedOrgs,
        activeOrgId: orgId,
        needsUpdate: true
      }
    }

    // Al actief, geen update nodig
    return {
      organizations: existingOrgs,
      activeOrgId: user.activeOrgId || orgId,
      needsUpdate: false
    }
  }

  // Nieuwe organisatie toevoegen
  const newOrg: UserOrganization = {
    orgId,
    role,
    domein,
    status: 'actief',
    addedAt: Timestamp.now()
  }

  return {
    organizations: [...existingOrgs, newOrg],
    activeOrgId: orgId,  // Zet nieuwe org als actief
    needsUpdate: true
  }
}

/**
 * Check of email hoort bij een van de user's organisatie domeinen
 */
export function emailMatchesUserOrgs(user: User | null | undefined, email: string): boolean {
  if (!user || !user.organizations) return false

  const emailDomain = email.toLowerCase().split('@')[1]

  return user.organizations.some(org =>
    org.domein.toLowerCase() === emailDomain ||
    org.domein.toLowerCase() === `@${emailDomain}`
  )
}

/**
 * Krijg organisatie naam display voor header
 */
export function getActiveOrgDisplay(user: User | null | undefined, orgName?: string): string {
  if (!user) return ''

  const level = getUserAccessLevel(user)
  const levelMap: Record<AccessLevel, string> = {
    'platform-admin': 'Platform Admin',
    'group-admin': 'Groep Admin',
    'school-admin': 'School Admin',
    'nascholingsbeheer': 'Nascholingsbeheer',
    'preventieadviseur': 'Preventieadviseur',
    'gebruikersbeheer': 'Gebruikersbeheerder',
    'issuer': 'Uitgever',
    'user': 'Gebruiker'
  }

  if (orgName) {
    return `${orgName} (${levelMap[level]})`
  }

  return levelMap[level]
}
